import React, { useEffect, useState, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useParams, useNavigate } from 'react-router-dom';
import MessageActions from '../components/MessageActions';
import ProductAd from '../components/ProductAd';
import {
  getCompanion, companionChat,
  listMemories, deleteMemory, clearAllMemories,
} from '../lib/companions';
import { bidturnsAnalyze, newSessionId } from '../lib/bidturns';
import { useUsage } from '../contexts/UsageContext';
import MemoryDrawer from '../components/MemoryDrawer';
import QuotaModal from '../components/QuotaModal';
import AiContentNotice from '../components/AiContentNotice';
import './CompanionChatPage.css';

function Bubble({ role, text, ad, intent, onRegenerate, onEdit }) {
  return (
    <div className={`mc-bubble mc-bubble-${role}`}>
      <div className="mc-bubble-body">
        {/* Assistant turns may contain markdown (asterisks for emphasis,
            occasional lists or code). User turns are rendered as raw text
            so a stray ** in their message doesn't suddenly bold things. */}
        {role === 'assistant'
          ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{text || ''}</ReactMarkdown>
          : text}
      </div>
      {/* Sponsored product card — renders only when BidTurns matched an
          actual product. Lives below the bubble body so it doesn't get
          mistaken for the assistant's own words. */}
      {role === 'assistant' && ad && <ProductAd ad={ad} intent={intent} />}
      <MessageActions
        role={role}
        text={text || ''}
        onRegenerate={onRegenerate}
        onEdit={onEdit}
        reportKind="companion"
      />
    </div>
  );
}

// Rotating waiting messages — a single static "X is thinking" stops
// reassuring the user past ~3s on a 5–7s wait. Cycling phrases makes the
// delay feel like active work. Each phrase displays for ~2.2s before the
// next one cross-fades in.
const TYPING_PHRASES = [
  (name) => `${name} is thinking`,
  () => `Gathering thoughts`,
  () => `Pulling the threads together`,
  () => `Almost there`,
  () => `Choosing the right words`,
];

function TypingIndicator({ name = 'Sutrum' }) {
  const [phraseIdx, setPhraseIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => {
      setPhraseIdx((i) => (i + 1) % TYPING_PHRASES.length);
    }, 2200);
    return () => clearInterval(t);
  }, []);
  const phrase = TYPING_PHRASES[phraseIdx](name);
  return (
    <div className="mc-bubble mc-bubble-assistant mc-typing">
      <div className="typing-row">
        <div className="typing"><span /><span /><span /></div>
        <span key={phraseIdx} className="typing-text typing-text-fade">{phrase}</span>
      </div>
    </div>
  );
}

function CompanionChatPage({ active, onSubscribe }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { bump: bumpUsage } = useUsage();

  const [companion, setCompanion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [memories, setMemories] = useState([]);
  const [quotaKind, setQuotaKind] = useState(null);
  const [quotaUsage, setQuotaUsage] = useState(null);

  const scrollerRef = useRef(null);
  const inputRef = useRef(null);
  // One BidTurns session per character chat — keeps purchase-intent
  // context cohesive within a single conversation. Reset to a new id
  // when the character changes (the useEffect on `id` below).
  const sessionIdRef = useRef(newSessionId());

  // Load companion + its server-side conversation on mount / id change.
  // Also stash a "last mitra" pointer in localStorage so the regular
  // ChatPage can offer a "Continue chatting with X" pill on next visit.
  useEffect(() => {
    let cancelled = false;
    if (!id) return;
    setLoading(true);
    setError(null);
    // Fresh BidTurns session per character — the previous character's
    // purchase-intent context doesn't carry over.
    sessionIdRef.current = newSessionId();
    (async () => {
      try {
        const c = await getCompanion(id);
        if (cancelled) return;
        setCompanion(c);
        setMessages(Array.isArray(c?.messages) ? c.messages : []);
        setMemories(Array.isArray(c?.memories) ? c.memories : []);
        try {
          if (c?.companionId) {
            localStorage.setItem('arthum.lastMitra.v1', JSON.stringify({
              companionId: c.companionId,
              name: c.name || '',
              avatarUrl: c.avatarUrl || null,
              archetype: c.archetype || null,
              updatedAt: Date.now(),
            }));
          }
        } catch { /* localStorage disabled */ }
      } catch (err) {
        if (cancelled) return;
        setError(err.message || 'Could not load this mitra.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  // Auto-scroll to bottom when messages grow.
  useEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, sending]);

  // Focus input when companion becomes available + page is active.
  useEffect(() => {
    if (active && companion && !sending) inputRef.current?.focus();
  }, [active, companion, sending]);

  const send = useCallback(async () => {
    const q = draft.trim();
    if (!q || sending || !companion) return;

    setDraft('');
    setError(null);

    // Optimistically append the user turn + a pending placeholder.
    const now = Date.now();
    const userMsg = { role: 'user', text: q, t: now };
    setMessages((ms) => [...ms, userMsg]);
    setSending(true);

    try {
      // Fire the LLM call and the BidTurns ad lookup in parallel —
      // ad is fully best-effort, never blocks the reply or surfaces
      // errors to the user. Same pattern as ChatPage.
      const [out, adPayload] = await Promise.all([
        companionChat(companion.companionId, { question: q }),
        bidturnsAnalyze({ query: q, sessionId: sessionIdRef.current }),
      ]);
      const asstMsg = {
        role: 'assistant',
        text: out?.answer || '',
        t: Date.now(),
        ad: adPayload?.adData || null,
        intent: adPayload?.intent || null,
      };
      setMessages((ms) => [...ms, asstMsg]);
      if (out?.usage) bumpUsage(out.usage);
      // Companion summary may have refreshed (memoryCount, etc).
      if (out?.companion) setCompanion((c) => ({ ...(c || {}), ...out.companion }));
    } catch (err) {
      if (err.code === 'QUOTA_EXCEEDED') {
        // Roll back the optimistic user turn — we never sent it.
        setMessages((ms) => ms.filter((m) => m !== userMsg));
        setQuotaKind(err.kind || 'DAILY_TOKEN_LIMIT');
        setQuotaUsage(err.usage || null);
      } else {
        setError(err.message || 'Something went wrong.');
        setMessages((ms) => ms.filter((m) => m !== userMsg));
        // EMPTY_ANSWER means the model returned nothing usable — most
        // often a transient R1 reasoning failure on retry. Drop the
        // question back into the input so the user can resend with one
        // click instead of retyping.
        if (err.code === 'EMPTY_ANSWER') setDraft(q);
        if (err.usage) bumpUsage(err.usage);
      }
    } finally {
      setSending(false);
    }
  }, [draft, sending, companion, bumpUsage]);

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const openDrawer = async () => {
    setDrawerOpen(true);
    try {
      const m = await listMemories(companion.companionId);
      setMemories(m || []);
    } catch (err) { console.warn('[memories]', err.message); }
  };

  const handleDeleteMemory = async (memoryId) => {
    try {
      const m = await deleteMemory(companion.companionId, memoryId);
      setMemories(m || []);
    } catch (err) { console.warn('[memory-delete]', err.message); }
  };

  const handleClearMemories = async () => {
    if (!window.confirm(`Erase everything ${companion.name} remembers about you?`)) return;
    try {
      await clearAllMemories(companion.companionId);
      setMemories([]);
    } catch (err) { console.warn('[memories-clear]', err.message); }
  };

  const initial = (companion?.name || '?').trim().charAt(0).toUpperCase();
  const fallbackAvatar = companion && !companion.isCustom
    ? `/mitras/${companion.archetype}.png`
    : null;
  const avatarSrc = companion?.avatarUrl || fallbackAvatar;

  return (
    <div className={`page mitra-chat-page ${active ? 'on' : ''}`}>
      {loading && (
        <div className="mc-loading">Loading…</div>
      )}

      {!loading && !companion && (
        <div className="mc-empty">
          <p>{error || 'Mitra not found.'}</p>
          <button
            className="mc-back-btn"
            onClick={() => {
              if (window.history.length > 1) navigate(-1);
              else navigate('/assistants');
            }}
          >Back</button>
        </div>
      )}

      {companion && (
        <>
          <header className="mc-header">
            <button
              className="mc-back"
              onClick={() => {
                // Prefer browser history so we land back on whichever
                // page launched us. Falls back to the Characters tab
                // (/assistants) when there's no previous entry (deep link).
                if (window.history.length > 1) navigate(-1);
                else navigate('/assistants');
              }}
              aria-label="Back"
            >
              <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <polyline points="15 18 9 12 15 6"/>
              </svg>
            </button>
            <div className="mc-avatar-sm">
              {avatarSrc
                ? <img src={avatarSrc} alt={companion.name} />
                : <span>{initial}</span>}
            </div>
            <div className="mc-headtxt">
              <div className="mc-name">{companion.name}</div>
              <div className="mc-tagline">{companion.tagline}</div>
            </div>
            <button className="mc-memory-btn" onClick={openDrawer} title="Memories">
              <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9c2.9 0 5.49 1.37 7.14 3.5"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
              <span className="mc-memory-count">{memories.length}</span>
            </button>
          </header>

          <div className="mc-scroller" ref={scrollerRef}>
            <div className="mc-thread">
              {messages.length === 0 && !sending && (
                <div className="mc-intro">
                  <div className="mc-intro-avatar">
                    {avatarSrc
                      ? <img src={avatarSrc} alt={companion.name} />
                      : <span>{initial}</span>}
                  </div>
                  <div className="mc-intro-name">{companion.name}</div>
                  <div className="mc-intro-tag">{companion.tagline}</div>
                  <div className="mc-intro-hint">Say hi to start.</div>
                </div>
              )}
              {messages.map((m, i) => {
                const isLast = i === messages.length - 1;
                return (
                  <Bubble
                    key={i}
                    role={m.role}
                    text={m.text}
                    ad={m.ad}
                    intent={m.intent}
                    onRegenerate={m.role === 'assistant' && isLast && !sending
                      ? () => {
                          const lastUser = [...messages].reverse().find((x) => x.role === 'user');
                          if (!lastUser?.text) return;
                          setMessages((cur) => cur.filter((x) => x !== m));
                          setDraft(lastUser.text);
                          // Defer send so the draft state has committed.
                          setTimeout(() => send(), 0);
                        }
                      : undefined}
                    onEdit={m.role === 'user' && isLast && !sending
                      ? () => setDraft(m.text || '')
                      : undefined}
                  />
                );
              })}
              {sending && <TypingIndicator name={companion?.name || 'Sutrum'} />}
              {error && <div className="mc-error">{error}</div>}
            </div>
          </div>

          <div className="mc-composer">
            <textarea
              ref={inputRef}
              className="mc-input"
              rows={1}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={`Message ${companion.name}…`}
              disabled={sending}
              maxLength={4000}
            />
            <button
              type="button"
              className="mc-send"
              onClick={send}
              disabled={!draft.trim() || sending}
              aria-label="Send"
            >
              <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13"/>
                <polygon points="22 2 15 22 11 13 2 9 22 2"/>
              </svg>
            </button>
          </div>
          {/* Persistent AI-content notice + Report affordance. Per-
              message Report is already available via MessageActions; this
              is the always-visible entry point for policy compliance. */}
          <AiContentNotice
            kind="companion"
            meta={{ page: 'companion-chat', companionId: companion?.companionId }}
          />

          {drawerOpen && (
            <MemoryDrawer
              companion={companion}
              memories={memories}
              onClose={() => setDrawerOpen(false)}
              onDelete={handleDeleteMemory}
              onClearAll={handleClearMemories}
            />
          )}

          {quotaKind && (
            <QuotaModal
              kind={quotaKind}
              usage={quotaUsage}
              onClose={() => { setQuotaKind(null); setQuotaUsage(null); }}
              onSubscribe={onSubscribe}
            />
          )}
        </>
      )}
    </div>
  );
}

export default CompanionChatPage;
